


























































import MultiSelect, { MultiSelectAction } from "@/components/MultiSelect.vue";
import { SlotObject } from "@/components/TimeSlotPicker.vue";
import VCheckbox from "@/components/VCheckbox.vue";
import ReportSubjectMixin, {
  ReportCompany,
  ReportSector,
} from "@/mixins/http/CustomReport/ReportProductMixin";
import { User } from "@/models";
import AdcontrolProfile from "@/models/AdcontrolProfile";
import MultiSelectBroadcaster, {
  Agency,
} from "@/models/MultiSelectBroadcaster";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { ProfileCapabilitiesService } from "@/services/Profile/CapabilitiesService";
import AgentsUserInfo from "@/models/Agents/UserInfo";

export type SubjectSelectData = {
  products: Array<ReportCompany>;
  sectors: Array<ReportSector>;
  applyClientDatabase: boolean;
  attachSectorCategoryClass: boolean;
  allProductTypes?: string[];
  allSectorTypes?: string[];
};

const PRODUCT_TYPES = ["company", "brand", "product"];
const SECTOR_TYPES = ["sector", "category", "class"];

@Component({
  components: {
    MultiSelect,
    VCheckbox,
  },
})
export default class ReportSubjectSelect extends Mixins(ReportSubjectMixin) {
  @Prop({ required: true }) readonly startsAt!: string;
  @Prop({ required: true }) readonly endsAt!: string;
  @Prop() readonly comparisonStartsAt!: string;
  @Prop() readonly comparisonEndsAt!: string;
  @Prop() readonly timeSlots!: SlotObject[];
  @Prop() readonly competitors?: MultiSelectBroadcaster[];
  @Prop() readonly agencies?: Agency[];
  @Prop({ default: false }) readonly productsDisabled!: boolean;
  @Prop({ default: false }) readonly sectorsDisabled!: boolean;
  @Prop({ default: () => [] }) readonly initialProducts!: ReportCompany[];
  @Prop({ default: () => [] }) readonly initialSectors!: ReportSector[];
  @Prop({ default: false }) readonly initialApplyClientDatabase!: boolean;
  @Prop({ default: false }) readonly initialAttachSectorCategoryClass!: boolean;
  @Prop({ default: true }) readonly clearItemsOnReselect!: boolean;
  @Prop({ default: () => [] }) readonly selectAllProductTypes!: string[];
  @Prop({ default: () => [] }) readonly selectAllSectorTypes!: string[];
  @Prop({ default: false }) readonly templateMode!: boolean;

  @Getter("profile") profile!: AdcontrolProfile;
  @Getter("user") user!: User;
  @Getter("agentUserInfos") readonly agentUserInfo?: AgentsUserInfo;

  products: ReportCompany[] = [];
  selectedProducts: ReportCompany[] = [];
  selectedSectors: ReportSector[] = [];
  sectors: ReportSector[] = [];
  loadingProducts = true;
  loadingSectors = true;
  applyClientDatabase = false;
  attachSectorCategoryClass = false;

  productActions: MultiSelectAction[] = [
    {
      name: "Aziende",
      filter: (element) => {
        return element.type == "company";
      },
    },
    {
      name: "Brand",
      filter: (element) => {
        return element.type == "brand";
      },
    },
    {
      name: "Prodotti",
      filter: (element) => {
        return element.type == "product";
      },
    },
  ];
  sectorActions: MultiSelectAction[] = [
    {
      name: "Settori",
      filter: (element) => {
        return element.type == "sector";
      },
    },
    {
      name: "Categorie",
      filter: (element) => {
        return element.type == "category";
      },
    },
    {
      name: "Classi",
      filter: (element) => {
        return element.type == "class";
      },
    },
  ];

  @Watch("initialProducts", { immediate: true })
  onInitialProductsUpdated(newVal: ReportCompany[]) {
    if (newVal === this.selectedProducts) return;
    const matched = this.matchSelectionToOptions(newVal, this.productsOptions);
    if (!this.sameSelection(matched, this.selectedProducts)) {
      this.selectedProducts = matched;
    }
  }

  @Watch("initialSectors", { immediate: true })
  onInitialSectorsUpdated(ip: ReportSector[]) {
    if (ip === this.selectedSectors) return;
    this.selectedSectors = this.matchSelectionToOptions(
      ip,
      this.classesOptions
    );
  }

  private sameSelection(a: Array<unknown>, b: Array<unknown>): boolean {
    return (
      a === b || (a.length === b.length && a.every((el, i) => el === b[i]))
    );
  }

  private matchSelectionToOptions<T extends { id?: number; type?: string }>(
    selection: T[],
    options: Array<{ id?: number; type?: string }>
  ): T[] {
    if (!options.length || !selection.length) return selection;
    const optionsByKey = new Map<string, T>();
    for (const option of options) {
      const key = `${option.type}:${option.id}`;
      if (!optionsByKey.has(key)) optionsByKey.set(key, option as T);
    }
    let changed = false;
    const matched = selection.map((item) => {
      const option = optionsByKey.get(`${item.type}:${item.id}`);
      if (option && option !== item) {
        changed = true;
        return option;
      }
      return item;
    });
    return changed ? matched : selection;
  }

  @Watch("initialApplyClientDatabase", { immediate: true })
  onInitialApplyClientDatabaseUpdated(newVal: boolean) {
    this.applyClientDatabase = newVal;
  }

  @Watch("initialAttachSectorCategoryClass", { immediate: true })
  onInitialAttachSectorCategoryClassUpdated(newVal: boolean) {
    this.attachSectorCategoryClass = newVal;
  }

  async mounted() {
    this.applyClientDatabase =
      this.forceAgentDatabase || this.initialApplyClientDatabase;
    await Promise.all([this.loadProducts(), this.loadClasses()]);
    this.selectedProducts = this.matchSelectionToOptions(
      this.selectedProducts,
      this.productsOptions
    );
    this.selectedSectors = this.matchSelectionToOptions(
      this.selectedSectors,
      this.classesOptions
    );
    this.$nextTick(() => this.applyInitialSelectAll());
  }

  applyInitialSelectAll() {
    if (!this.selectAllProductTypes.length && !this.selectAllSectorTypes.length)
      return;

    const productsSelect = this.$refs.productsSelect as MultiSelect | undefined;
    for (const type of this.selectAllProductTypes) {
      const action = this.findActionByType(this.productActions, type);
      if (action) productsSelect?.activateAction(action);
    }

    const sectorsSelect = this.$refs.sectorsSelect as MultiSelect | undefined;
    for (const type of this.selectAllSectorTypes) {
      const action = this.findActionByType(this.sectorActions, type);
      if (action) sectorsSelect?.activateAction(action);
    }

    this.$emit("select-all-applied");
  }

  private findActionByType(
    actions: MultiSelectAction[],
    type: string
  ): MultiSelectAction | undefined {
    return actions.find((action) => action.filter({ name: "", type }));
  }

  private fullySelectedTypes(
    selected: Array<{ id?: number; type?: string }>,
    options: Array<{ id?: number; type?: string }>,
    types: string[]
  ): string[] {
    return types.filter((type) => {
      const optionIds = options
        .filter((option) => option.type === type)
        .map((option) => option.id);
      if (!optionIds.length) return false;
      const selectedIds = new Set(
        selected
          .filter((element) => element.type === type)
          .map((element) => element.id)
      );
      return optionIds.every((id) => selectedIds.has(id));
    });
  }

  get allProductTypesSelected(): string[] {
    return this.fullySelectedTypes(
      this.selectedProducts,
      this.productsOptions,
      PRODUCT_TYPES
    );
  }

  get allSectorTypesSelected(): string[] {
    return this.fullySelectedTypes(
      this.selectedSectors,
      this.classesOptions,
      SECTOR_TYPES
    );
  }

  async loadProducts() {
    const type = this.isRadio ? "radio" : "tv";
    this.products = this.templateMode
      ? await this.getTemplateProducts(type)
      : await this.getProducts(
          type,
          this.startsAt,
          this.endsAt,
          this.comparisonStartsAt,
          this.comparisonEndsAt,
          this.timeSlots,
          this.competitors,
          this.agencies
        );
    this.loadingProducts = false;
  }

  async loadClasses() {
    const type = this.isRadio ? "radio" : "tv";
    this.sectors = this.templateMode
      ? await this.getTemplateClasses(type)
      : await this.getClasses(
          type,
          this.startsAt,
          this.endsAt,
          this.comparisonStartsAt,
          this.comparisonEndsAt,
          this.timeSlots,
          this.competitors,
          this.agencies
        );
    this.selectedSectors = this.initialSectors;
    this.loadingSectors = false;
  }

  get forceAgentDatabase(): boolean {
    return (
      this.agentsProfileAssociationDataEnabled && !this.userIsAgentSuperUser
    );
  }

  get loading(): boolean {
    return this.loadingProducts || this.loadingSectors;
  }

  @Watch("loading", { immediate: true })
  onLoadingUpdated(value: boolean) {
    this.$emit("update:loading", value);
  }

  get productsAreDisabled(): boolean {
    return this.productsDisabled || this.applyClientDatabase;
  }

  get sectorsAreDisabled(): boolean {
    return this.sectorsDisabled || this.applyClientDatabase;
  }

  get userIsAgentSuperUser(): boolean {
    return this.agentUserInfo?.superUser ?? false;
  }

  get attachSectorCategoryClassDisabled(): boolean {
    return !this.applyClientDatabase && !this.onlyProductsSelected;
  }

  get productsOptions() {
    if (this.products.length == 0) {
      return [];
    }

    const options = [];

    for (const company of this.products) {
      options.push({
        id: company.id,
        name: company.name,
        type: "company",
        nestLevel: 1,
      });

      for (const brand of company.brands) {
        options.push({
          id: brand.id,
          name: this.toJoinedString([company.name, brand.name]),
          type: "brand",
          nestLevel: 2,
        });

        for (const product of brand.products) {
          options.push({
            id: product.id,
            name: this.toJoinedString([company.name, brand.name, product.name]),
            type: "product",
            nestLevel: 3,
          });
        }
      }
    }

    return options;
  }

  toJoinedString(list: Array<string>, sep = " > ") {
    return list.join(sep);
  }

  get classesOptions() {
    if (this.sectors.length == 0) {
      return [];
    }

    const options = [];

    for (const sector of this.sectors) {
      options.push({
        id: sector.id,
        name: sector.name,
        type: "sector",
        nestLevel: 1,
      });

      for (const category of sector.categories) {
        options.push({
          id: category.id,
          name: this.toJoinedString([sector.name, category.name]),
          type: "category",
          nestLevel: 2,
        });

        for (const productClass of category.classes) {
          options.push({
            id: productClass.id,
            name: this.toJoinedString([
              sector.name,
              category.name,
              productClass.name,
            ]),
            type: "class",
            nestLevel: 3,
          });
        }
      }
    }

    return options;
  }

  get company() {
    return this.profile.accountCompany;
  }

  get isRadio(): boolean {
    return this.profile.accountType == "radio";
  }

  get agentsProfileAssociationDataEnabled(): boolean {
    return ProfileCapabilitiesService.agentsModuleEnabled(
      this.user.profile,
      this.agentUserInfo
    );
  }

  get profileName(): string {
    return this.profile.name;
  }

  get onlyProductsSelected(): boolean {
    return (
      this.selectedProducts.length > 0 &&
      this.selectedSectors.length == 0 &&
      this.selectedProducts.every((p) => p.type === "product")
    );
  }

  @Watch("selectedProducts")
  onProductsUpdated(products: ReportCompany[]) {
    if (this.attachSectorCategoryClassDisabled) {
      this.attachSectorCategoryClass = false;
    }
    this.$emit("selected", {
      products,
      sectors: this.selectedSectors,
      applyClientDatabase: this.applyClientDatabase,
      attachSectorCategoryClass: this.attachSectorCategoryClass,
      allProductTypes: this.allProductTypesSelected,
      allSectorTypes: this.allSectorTypesSelected,
    });
  }

  @Watch("selectedSectors")
  onSectorsUpdated(sectors: ReportSector[]) {
    this.$emit("selected", {
      products: this.selectedProducts,
      sectors,
      applyClientDatabase: this.applyClientDatabase,
      attachSectorCategoryClass: this.attachSectorCategoryClass,
      allProductTypes: this.allProductTypesSelected,
      allSectorTypes: this.allSectorTypesSelected,
    });
    this.selectedSectors = sectors;
  }

  @Watch("attachSectorCategoryClass")
  onAttachSectorCategoryClassUpdated(attachSectorCategoryClass: boolean) {
    if (!this.applyClientDatabase) {
      this.$emit("selected", {
        products: this.selectedProducts,
        sectors: this.selectedSectors,
        applyClientDatabase: this.applyClientDatabase,
        attachSectorCategoryClass,
        allProductTypes: this.allProductTypesSelected,
        allSectorTypes: this.allSectorTypesSelected,
      });
      return;
    }
    this.$emit("selected", {
      products: this.products.flatMap((p) => {
        return p.brands.flatMap((b) => {
          return b.products.map((product) => {
            return {
              id: product.id,
              name: `${p.name} > ${b.name} > ${product.name}`,
              type: "product",
            };
          });
        });
      }),
      sectors: [],
      applyClientDatabase: this.applyClientDatabase,
      attachSectorCategoryClass,
      allProductTypes: [],
      allSectorTypes: [],
    });
  }

  @Watch("applyClientDatabase")
  onApplyClientDatabase(applyClientDatabase: boolean) {
    if (!applyClientDatabase) {
      if (this.attachSectorCategoryClassDisabled) {
        this.attachSectorCategoryClass = false;
      }
      this.$emit("selected", {
        products: this.selectedProducts,
        sectors: this.selectedSectors,
        applyClientDatabase,
        attachSectorCategoryClass: this.attachSectorCategoryClass,
        allProductTypes: this.allProductTypesSelected,
        allSectorTypes: this.allSectorTypesSelected,
      });
      return;
    }
    this.$emit("selected", {
      products: this.products.flatMap((p) => {
        return p.brands.flatMap((b) => {
          return b.products.map((product) => {
            return {
              id: product.id,
              name: `${p.name} > ${b.name} > ${product.name}`,
              type: "product",
            };
          });
        });
      }),
      sectors: [],
      applyClientDatabase,
      attachSectorCategoryClass: this.attachSectorCategoryClass,
      allProductTypes: [],
      allSectorTypes: [],
    });
  }
}
